/**
 * ColumnHeader Component
 * Renders a qualification column header with stats, progress bar, and tooltip
 */

import { memo } from 'react';
import { EyeOff } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const ColumnHeader = memo(({
  qual,
  index,
  isHighlighted,
  qualStats,
  toggleColumnHighlight,
  handleRemoveQualificationFromMatrix,
  openQualificationGapId,
  setOpenQualificationGapId,
  onContextMenu
}) => {
  // Default stats when compliance RPC hasn't returned data for this qualification yet
  const stats = qualStats || { required: 0, valid: 0, validCrossSpace: 0, expiring: 0, expiringCrossSpace: 0, expired: 0, missing: 0, booked: 0, quoting: 0, compliancePercentage: 0 };

  // Sortable (column reordering via drag-and-drop)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: qual.id });

  // Constrain transform to horizontal axis only
  const constrainedTransform = transform ? { ...transform, y: 0 } : null;

  const sortableStyle = {
    width: '112px',
    transform: CSS.Transform.toString(constrainedTransform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 40 : undefined
  };

  return (
    <th
      ref={setNodeRef}
      style={sortableStyle}
      className={`sticky top-[46px] z-30 border-b border-r border-gray-200 p-0 group relative h-[112px] transition-colors duration-200 ${isHighlighted ? 'bg-blue-50 border-l-2 border-r-2 border-t-2 border-blue-400' : 'bg-white'}`}
      {...attributes}
      {...listeners}
    >
      <div className="h-full flex flex-col overflow-hidden relative">
        {/* Main content area - click to select column */}
        <TooltipProvider delayDuration={300} skipDelayDuration={0}>
          <Tooltip delayDuration={300} disableHoverableContent={false}>
            <TooltipTrigger asChild>
              <div
                className="flex-1 flex flex-col px-2 py-2 cursor-pointer transition-all duration-200 hover:bg-gray-50"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleColumnHighlight(qual.id);
                }}
                onContextMenu={(e) => onContextMenu && onContextMenu(qual, qualStats, e)}
              >
                {/* Qualification name - grows to take available space */}
                <div className="text-center w-full px-1 flex-1 flex items-center justify-center">
                  <p className="text-xs font-semibold text-gray-900 leading-tight line-clamp-2">
                    {qual.name}
                  </p>
                </div>
                {/* Progress bar - always at bottom */}
                <div className="w-full px-1 mt-1">
                  <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden flex">
                    {/* Segmented progress bar */}
                    {(() => {
                      const validPct = stats.required > 0 ? ((stats.valid || 0) / stats.required) * 100 : 0;
                      const validCrossPct = stats.required > 0 ? ((stats.validCrossSpace || 0) / stats.required) * 100 : 0;
                      const expiringPct = stats.required > 0 ? ((stats.expiring || 0) / stats.required) * 100 : 0;
                      const expiringCrossPct = stats.required > 0 ? ((stats.expiringCrossSpace || 0) / stats.required) * 100 : 0;
                      const expiredPct = stats.required > 0 ? ((stats.expired || 0) / stats.required) * 100 : 0;
                      const missingPct = stats.required > 0 ? ((stats.missing || 0) / stats.required) * 100 : 0;

                      return (
                        <>
                          {/* Valid (Green) */}
                          {validPct > 0 && (
                            <div className="h-1.5 bg-green-500 transition-all" style={{ width: `${validPct}%` }} />
                          )}
                          {/* Valid Cross-Space (Light Green) */}
                          {validCrossPct > 0 && (
                            <div className="h-1.5 bg-green-300 transition-all" style={{ width: `${validCrossPct}%` }} />
                          )}
                          {/* Expiring (Amber) */}
                          {expiringPct > 0 && (
                            <div className="h-1.5 bg-amber-500 transition-all" style={{ width: `${expiringPct}%` }} />
                          )}
                          {/* Expiring Cross-Space (Light Amber) */}
                          {expiringCrossPct > 0 && (
                            <div className="h-1.5 bg-amber-300 transition-all" style={{ width: `${expiringCrossPct}%` }} />
                          )}
                          {/* Expired (Red) */}
                          {expiredPct > 0 && (
                            <div className="h-1.5 bg-red-500 transition-all" style={{ width: `${expiredPct}%` }} />
                          )}
                          {/* Missing (Red) */}
                          {missingPct > 0 && (
                            <div className="h-1.5 bg-red-500 transition-all" style={{ width: `${missingPct}%` }} />
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <div className="text-[9px] text-gray-500 text-center mt-0.5 font-medium">
                    {stats.compliancePercentage}%
                  </div>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs bg-white border border-gray-200 shadow-lg p-3" sideOffset={5}>
              {/* Header with qualification name */}
              <div className="font-semibold text-sm text-gray-900 mb-1">{qual.name}</div>
              {qual.spaces && qual.spaces.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2 pb-2 border-b border-gray-100">
                  {qual.spaces.map(s => (
                    <span key={s.id} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                      {s.name}
                    </span>
                  ))}
                </div>
              )}
              {!(qual.spaces && qual.spaces.length > 0) && (
                <div className="mb-1 pb-2 border-b border-gray-100" />
              )}

              {/* Compliance percentage - highlighted */}
              <div className={`rounded-lg p-3 mb-3 ${
                stats.compliancePercentage >= 80
                  ? 'bg-green-50 border border-green-200'
                  : stats.compliancePercentage >= 50
                  ? 'bg-yellow-50 border border-yellow-200'
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-xs font-medium text-gray-600">Overall Compliance</span>
                  <span className={`text-lg font-bold ${
                    stats.compliancePercentage >= 80 ? 'text-green-600'
                    : stats.compliancePercentage >= 50 ? 'text-yellow-600'
                    : 'text-red-600'
                  }`}>
                    {stats.compliancePercentage}%
                  </span>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-green-50 rounded-md px-2 py-1.5 border border-green-200">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center justify-between">
                      <span className="text-green-700 font-medium">Valid</span>
                      <span className="font-bold text-green-600">{(stats.valid || 0) + (stats.validCrossSpace || 0)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-green-600 opacity-70">
                      <span>Assigned: {stats.valid || 0}</span>
                      <span>Unassigned: {stats.validCrossSpace || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 rounded-md px-2 py-1.5 border border-yellow-200">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center justify-between">
                      <span className="text-yellow-700 font-medium">Expiring</span>
                      <span className="font-bold text-yellow-600">{(stats.expiring || 0) + (stats.expiringCrossSpace || 0)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-yellow-600 opacity-70">
                      <span>Assigned: {stats.expiring || 0}</span>
                      <span>Unassigned: {stats.expiringCrossSpace || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-red-50 rounded-md px-2 py-1.5 border border-red-200">
                  <div className="flex items-center justify-between">
                    <span className="text-red-700 font-medium">Expired</span>
                    <span className="font-bold text-red-600">{stats.expired}</span>
                  </div>
                </div>
                <div className="bg-red-50 rounded-md px-2 py-1.5 border border-red-200">
                  <div className="flex items-center justify-between">
                    <span className="text-red-700 font-medium">Missing</span>
                    <span className="font-bold text-red-600">{stats.missing}</span>
                  </div>
                </div>
                <div className="bg-primary/10 rounded-md px-2 py-1.5 border border-primary/20">
                  <div className="flex items-center justify-between">
                    <span className="text-primary font-medium">Booked</span>
                    <span className="font-bold text-primary">{stats.booked}</span>
                  </div>
                </div>
                <div className="bg-primary/10 rounded-md px-2 py-1.5 border border-primary/20">
                  <div className="flex items-center justify-between">
                    <span className="text-primary font-medium">Quoting</span>
                    <span className="font-bold text-primary">{stats.quoting}</span>
                  </div>
                </div>
              </div>

              {/* Required vs Assigned */}
              <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Required:</span>
                  <span className="font-bold text-gray-900">{stats.required}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Assigned:</span>
                  <span className="font-bold text-gray-900">{stats.totalAssigned}</span>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* GAP Analysis Dialog for Qualification */}
      <Dialog open={openQualificationGapId === qual.id} onOpenChange={(open) => setOpenQualificationGapId(open ? qual.id : null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{qual.name} - GAP Analysis</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {qual.description && (
              <p className="text-sm text-gray-600 pb-3 border-b">{qual.description}</p>
            )}

            {/* Compliance Overview */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600">Overall Compliance</span>
                <span className={`text-2xl font-bold ${stats.compliancePercentage >= 80 ? 'text-green-600' : stats.compliancePercentage >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {stats.compliancePercentage}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                {/* Compliant portion (valid + expiring) - green */}
                <div
                  className="h-2 bg-green-500 transition-all"
                  style={{ width: `${stats.compliancePercentage}%` }}
                />
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-green-50 rounded-lg p-2.5 border border-green-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-green-700 font-medium">Valid</span>
                  <span className="text-lg font-bold text-green-600">{stats.valid}</span>
                </div>
              </div>

              <div className="bg-yellow-50 rounded-lg p-2.5 border border-yellow-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-yellow-700 font-medium">Expiring</span>
                  <span className="text-lg font-bold text-yellow-600">{stats.expiring}</span>
                </div>
              </div>

              <div className="bg-red-50 rounded-lg p-2.5 border border-red-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-red-700 font-medium">Expired</span>
                  <span className="text-lg font-bold text-red-600">{stats.expired}</span>
                </div>
              </div>

              <div className="bg-red-50 rounded-lg p-2.5 border border-red-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-red-700 font-medium">Missing</span>
                  <span className="text-lg font-bold text-red-600">{stats.missing}</span>
                </div>
              </div>

              <div className="bg-primary/10 rounded-lg p-2.5 border border-primary/20">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-primary font-medium">Booked</span>
                  <span className="text-lg font-bold text-primary">{stats.booked}</span>
                </div>
              </div>

              <div className="bg-primary/10 rounded-lg p-2.5 border border-primary/20">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-primary font-medium">Quoting</span>
                  <span className="text-lg font-bold text-primary">{stats.quoting}</span>
                </div>
              </div>
            </div>

            {/* Total Summary */}
            <div className="border-t pt-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Required</span>
                <span className="text-xl font-bold text-blue-600">{stats.required}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Total Assigned</span>
                <span className="text-xl font-bold text-gray-900">{stats.totalAssigned}</span>
              </div>
            </div>
          </div>

        </DialogContent>
      </Dialog>
    </th>
  );
});

ColumnHeader.displayName = 'ColumnHeader';

export default ColumnHeader;
